import React from 'react';
import type { AppData } from '../types';
import { Card } from './UI';

interface HonorairesViewProps {
  data: AppData;
}

export function HonorairesView({ data }: HonorairesViewProps) {
  const dossiers = data.dossiers;
  const temps = data.temps;

  // Calculate totals
  const totalHonorairesForfeits = dossiers
    .filter((d) => d.modeFacturation === 'forfait')
    .reduce((sum, d) => sum + d.honoraires, 0);

  const totalHonorairesHoraires = dossiers
    .filter((d) => d.modeFacturation === 'horaire')
    .reduce((sum, d) => sum + d.honoraires, 0);

  const totalHonoraires = totalHonorairesForfeits + totalHonorairesHoraires;
  const totalPaid = dossiers.reduce((sum, d) => sum + d.montantRegle, 0);
  const totalUnpaid = totalHonoraires - totalPaid;
  const percentPaid = totalHonoraires > 0 ? Math.round((totalPaid / totalHonoraires) * 100) : 0;

  const tempsAmount = temps.reduce((sum, t) => sum + (t.montant || 0), 0);

  // Group by dossier
  const dossiersWithDetails = dossiers.map((d) => ({
    ...d,
    tempsAmount: temps.filter((t) => t.dossier === d.id).reduce((sum, t) => sum + (t.montant || 0), 0),
  }));

  // Payment status breakdown
  const statusCount = {
    'En attente': dossiers.filter((d) => d.statutPaiement === 'En attente').length,
    'Acompte versé': dossiers.filter((d) => d.statutPaiement === 'Acompte versé').length,
    'Partiellement réglé': dossiers.filter((d) => d.statutPaiement === 'Partiellement réglé').length,
    'Soldé': dossiers.filter((d) => d.statutPaiement === 'Soldé').length,
    'Impayé': dossiers.filter((d) => d.statutPaiement === 'Impayé').length,
  };

  const statusColors = {
    'En attente': '#9e6a03',
    'Acompte versé': '#0969da',
    'Partiellement réglé': '#1f883d',
    'Soldé': '#1a7f37',
    'Impayé': '#cf222e',
  };

  return (
    <div>
      <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>Honoraires</h1>

      {/* Main stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            TOTAL HONORAIRES
          </p>
          <p style={{ margin: '0 0 0.25rem', fontSize: 24, fontWeight: 600 }}>
            {totalHonoraires}€
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Forfait: {totalHonorairesForfeits}€ | Horaire: {totalHonorairesHoraires}€
          </p>
        </Card>

        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            MONTANT RECOUVRÉ
          </p>
          <p style={{ margin: '0 0 0.25rem', fontSize: 24, fontWeight: 600, color: '#1a7f37' }}>
            {totalPaid}€
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {percentPaid}% des honoraires
          </p>
        </Card>

        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            MONTANT EN ATTENTE
          </p>
          <p style={{ margin: '0 0 0.25rem', fontSize: 24, fontWeight: 600, color: '#cf222e' }}>
            {totalUnpaid}€
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {100 - percentPaid}% à recouvrir
          </p>
        </Card>

        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)', fontWeight: 500 }}>
            TEMPS ENREGISTRÉ
          </p>
          <p style={{ margin: '0 0 0.25rem', fontSize: 24, fontWeight: 600 }}>
            {tempsAmount}€
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
            {temps.length} enregistrement(s)
          </p>
        </Card>
      </div>

      {/* Payment status */}
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '1rem' }}>Statut de paiement</h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.75rem',
          marginBottom: '2rem',
        }}
      >
        {Object.entries(statusCount).map(([status, count]) => (
          <Card key={status}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: statusColors[status as keyof typeof statusColors],
                }}
              />
              <div>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  {status}
                </p>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                  {count}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Detailed table */}
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: '1rem' }}>Par dossier</h2>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 12,
          }}
        >
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Dossier</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Mode</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Honoraires</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Temps</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Réglé</th>
              <th style={{ textAlign: 'right', padding: '0.75rem', fontWeight: 600 }}>Restant</th>
              <th style={{ textAlign: 'left', padding: '0.75rem', fontWeight: 600 }}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {dossiersWithDetails.map((d) => (
              <tr
                key={d.id}
                style={{
                  borderBottom: '0.5px solid var(--color-border-secondary)',
                  backgroundColor: 'var(--color-background-secondary)',
                }}
              >
                <td style={{ padding: '0.75rem' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 500 }}>{d.ref}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                      {d.client}
                    </p>
                  </div>
                </td>
                <td style={{ padding: '0.75rem' }}>
                  {d.modeFacturation === 'forfait' ? '📌 Forfait' : `💶 Horaire (${d.tauxHoraire}€/h)`}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 600 }}>
                  {d.honoraires}€
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                  {d.tempsAmount > 0 ? `${d.tempsAmount}€` : '—'}
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', color: '#1a7f37', fontWeight: 600 }}>
                  {d.montantRegle}€
                </td>
                <td style={{ padding: '0.75rem', textAlign: 'right', color: '#cf222e', fontWeight: 600 }}>
                  {d.honoraires - d.montantRegle}€
                </td>
                <td style={{ padding: '0.75rem' }}>
                  <span
                    style={{
                      background: statusColors[d.statutPaiement],
                      color: '#ffffff',
                      padding: '2px 8px',
                      borderRadius: 'var(--border-radius-sm)',
                      fontSize: 10,
                      fontWeight: 500,
                    }}
                  >
                    {d.statutPaiement}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
